// src/components/festival/PublicHomePage.tsx
// DROP 19 — ERUE Festival Suite Public Homepage
// Palace hero + Logo + Countdown + Action cards + Footer

import React, { useState, useEffect, useCallback } from "react";
import {
  Heart, Store, Globe, Ticket, Calendar,
  ChevronRight, ArrowRight
} from "lucide-react";

import HomepageSections from './HomepageSections';

interface Props {
  onNavigate: (view: string) => void;
  currentUser?: { email: string; name?: string } | null;
  onLoginClick: () => void;
}



export default function PublicHomePage({ onNavigate, currentUser, onLoginClick }: Props) {

  const [social,     setSocial]     = useState<any>({});
  const [campaigns,  setCampaigns]  = useState<any[]>([]);
  const [vendorCats, setVendorCats] = useState<number>(0);


  // Load social links + campaigns
  useEffect(() => {
    fetch("/api/public/social-links")
      .then(r => r.json())
      .then(d => { if (d.success) setSocial(d.links || {}); })
      .catch(() => {});
    fetch("/api/fundraising/campaigns")
      .then(r => r.json())
      .then(d => { if (d.success) setCampaigns(d.campaigns || []); })
      .catch(() => {});
    fetch("/api/vendor/categories")
      .then(r => r.json())
      .then(d => { if (d.success) setVendorCats((d.categories||[]).filter((c: any) => c.isOpen).length); })
      .catch(() => {});
  }, []);



  const ACTION_CARDS = [
    {
      icon: <Heart size={28}/>,
      title: "Donate & Support",
      desc: "Help make ERUE Festival 2026 a magnificent celebration. Every contribution counts.",
      cta: "Donate Now",
      view: "fundraising",
      color: "#C9A84C",
      bg: "rgba(201,168,76,0.08)",
      border: "rgba(201,168,76,0.2)",
    },
    {
      icon: <Store size={28}/>,
      title: "Vendor Registration",
      desc: `${vendorCats} categories open. Register as DJ, food vendor, canteen, photographer & more.`,
      cta: "Register Now",
      view: "vendor-register",
      color: "#4ADE80",
      bg: "rgba(74,222,128,0.08)",
      border: "rgba(74,222,128,0.2)",
    },
    {
      icon: <Globe size={28}/>,
      title: "26 Communities",
      desc: "Explore all 22 Uzairue villages and 4 special communities participating in the festival.",
      cta: "Explore",
      view: "communities",
      color: "#60A5FA",
      bg: "rgba(96,165,250,0.08)",
      border: "rgba(96,165,250,0.2)",
    },
    {
      icon: <Ticket size={28}/>,
      title: "Festival Raffle",
      desc: "Participate in the ERUE Festival raffle draw. Exciting prizes to be won!",
      cta: "Join Raffle",
      view: "raffle",
      color: "#F472B6",
      bg: "rgba(244,114,182,0.08)",
      border: "rgba(244,114,182,0.2)",
    },
  ];

  const HIGHLIGHTS = [
    { icon:"🎭", title:"Cultural Displays",   desc:"Traditional dances, costumes and heritage exhibitions from all 26 communities" },
    { icon:"⚽", title:"Sports Competition",  desc:"Inter-community sports events — football, athletics and traditional games" },
    { icon:"🎵", title:"Live Entertainment",  desc:"Renowned artists, cultural performances and the grand festival concert" },
    { icon:"👑", title:"Royal Durbar",        desc:"The Ogieneni of Uzairue Kingdom presides over the royal court ceremony" },
    { icon:"🏆", title:"Awards Night",        desc:"Recognition of outstanding community members and diaspora contributors" },
    { icon:"🎇", title:"Grand Finale",        desc:"Spectacular closing ceremony on Saturday 30 December 2026" },
  ];

  return (
    <div style={{ background:"#070D0A", minHeight:"100vh" }}>

{/* ═══ DROP 20: Palace Hero + Logo (no white bg) + Ogieneni Portrait ═══ */}
<section
  className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
  style={{ minHeight: '100vh' }}
>
  {/* Palace background */}
  <div className="absolute inset-0 z-0">
    <img
      src="https://res.cloudinary.com/uuovmrek/image/upload/uzairuepalace001"
      alt="Palace of the Ogieneni of Uzairue Kingdom"
      className="w-full h-full object-cover object-center"
      style={{ filter: 'brightness(0.35) saturate(1.1)' }}
    />
    {/* Gradient overlay — deep green → transparent → deep green */}
    <div className="absolute inset-0"
      style={{ background:
        'linear-gradient(to bottom, #070D0Af0 0%, #070D0A80 30%, #070D0A60 60%, #070D0Af0 100%)'
      }} />
  </div>

  {/* Content layer */}
  <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8
                   flex flex-col items-center text-center pt-24 pb-16 gap-8">

    {/* ── Top: Logo + Kingdom name ── */}
    <div className="flex flex-col items-center gap-4">
      {/* Logo — mix-blend-mode:multiply removes white background on dark bg */}
      <div className="relative">
        <div
          className="w-28 h-28 rounded-full flex items-center justify-center
                     border-2 border-[#C9A84C]/50 shadow-2xl shadow-black/60
                     overflow-hidden"
          style={{ background: 'rgba(15,81,50,0.3)', backdropFilter: 'blur(4px)' }}
        >
          <img
            src="https://res.cloudinary.com/uuovmrek/image/upload/e_bgremoval/uzairuelogo01"
            alt="ERUE Festival — Uzairue Kingdom"
            className="w-full h-full object-contain"
            style={{ mixBlendMode: 'screen', filter: 'contrast(1.1) brightness(1.05)' }}
            onError={(e) => {
              // Fallback if bgremoval fails: use screen blend mode on original
              (e.target as HTMLImageElement).src = "https://res.cloudinary.com/uuovmrek/image/upload/uzairuelogo01";
              (e.target as HTMLImageElement).style.mixBlendMode = 'screen';
            }}
          />
        </div>
        {/* Gold ring pulse */}
        <div className="absolute inset-0 rounded-full border-2 border-[#C9A84C]/30
                        animate-ping" style={{ animationDuration: '3s' }} />
      </div>

      <div>
        <p className="text-[#C9A84C] text-xs tracking-[0.4em] uppercase font-medium mb-1">
          Annual
        </p>
        <h1 className="text-[#EDE8DC] font-bold leading-tight"
            style={{ fontFamily: 'Cinzel, serif', fontSize: 'clamp(1.8rem, 5vw, 3.5rem)' }}>
          ERUE Festival
        </h1>
        <p className="text-[#C9A84C] text-base sm:text-lg tracking-widest uppercase mt-1"
           style={{ fontFamily: 'Cinzel, serif' }}>
          of Uzairue Kingdom
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="h-px w-12 bg-[#C9A84C]/40" />
        <p className="text-[#EDE8DC]/70 text-xs tracking-[0.3em] uppercase">
          One People · One Kingdom · One Destiny
        </p>
        <div className="h-px w-12 bg-[#C9A84C]/40" />
      </div>
    </div>

    {/* ── Middle: Ogieneni Portrait ── */}
    <div className="flex flex-col items-center gap-3">
      <div
        className="relative w-48 h-56 sm:w-56 sm:h-64 rounded-2xl overflow-hidden
                   border-2 border-[#C9A84C]/40 shadow-2xl shadow-black/70"
        style={{ background: 'rgba(10,61,38,0.4)' }}
      >
        <img
          src="https://res.cloudinary.com/uuovmrek/image/upload/ogienenimai002"
          alt="HRH The Ogieneni of Uzairue Kingdom"
          className="w-full h-full object-cover object-top"
          onError={(e) => {
            // Placeholder if portrait not yet uploaded
            const el = e.target as HTMLImageElement;
            el.style.display = 'none';
            const parent = el.parentElement;
            if (parent) {
              parent.innerHTML = `
                <div style="width:100%;height:100%;display:flex;flex-direction:column;
                  align-items:center;justify-content:center;gap:8px;
                  background:rgba(15,81,50,0.3)">
                  <div style="font-size:3rem">👑</div>
                  <p style="color:#C9A84C;font-size:0.7rem;text-align:center;padding:0 8px;
                    font-family:Cinzel,serif;letter-spacing:0.1em">
                    HRH The Ogieneni<br/>of Uzairue Kingdom
                  </p>
                </div>`;
            }
          }}
        />
        {/* Gold shimmer overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-16"
          style={{ background: 'linear-gradient(to top, rgba(201,168,76,0.15), transparent)' }} />
      </div>
      <div className="text-center">
        <p className="text-[#C9A84C] text-xs font-semibold tracking-widest uppercase"
           style={{ fontFamily: 'Cinzel, serif' }}>
          Royal Patron
        </p>
        <p className="text-[#EDE8DC]/80 text-sm mt-0.5">
          HRH The Ogieneni of Uzairue Kingdom
        </p>
      </div>
    </div>

    {/* ── Bottom: Festival dates + CTA ── */}
    <div className="flex flex-col items-center gap-4 w-full max-w-lg">
      <div className="flex items-center gap-3 px-6 py-3
                      bg-[#0F5132]/60 border border-[#C9A84C]/30 rounded-xl
                      backdrop-blur-sm w-full justify-center">
        <span className="text-[#C9A84C] text-2xl">🗓️</span>
        <div className="text-left">
          <p className="text-[#C9A84C] text-xs tracking-widest uppercase font-semibold">
            Festival Dates
          </p>
          <p className="text-[#EDE8DC] text-sm font-medium">
            December 24 – 30, 2026
          </p>
          <p className="text-[#EDE8DC]/60 text-xs">
            Grand Finale: Wednesday, 30 December 2026
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 px-6 py-3
                      bg-[#0F5132]/40 border border-[#C9A84C]/20 rounded-xl
                      backdrop-blur-sm w-full justify-center">
        <span className="text-[#C9A84C] text-xl">📍</span>
        <div className="text-left">
          <p className="text-[#EDE8DC]/80 text-xs">
            Palace of the Ogieneni · Jattu-Uzairue
          </p>
          <p className="text-[#EDE8DC]/50 text-xs">
            Etsako West LGA, Edo State, Nigeria
          </p>
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="flex flex-wrap gap-3 justify-center mt-2">
        <button
          onClick={() => onNavigate('programme')}
          className="px-6 py-2.5 rounded-lg font-semibold text-sm
                     bg-[#C9A84C] text-[#070D0A]
                     hover:bg-[#E2C97E] transition-all duration-200 shadow-lg"
          style={{ fontFamily: 'Cinzel, serif' }}
        >
          View Programme
        </button>
        <button
          onClick={() => onNavigate('communities')}
          className="px-6 py-2.5 rounded-lg font-semibold text-sm
                     bg-transparent text-[#C9A84C]
                     border border-[#C9A84C]/50
                     hover:bg-[#C9A84C]/10 transition-all duration-200"
          style={{ fontFamily: 'Cinzel, serif' }}
        >
          Our Communities
        </button>
        <button
          onClick={() => onNavigate('fundraising')}
          className="px-6 py-2.5 rounded-lg font-semibold text-sm
                     bg-transparent text-[#EDE8DC]/70
                     border border-[#EDE8DC]/20
                     hover:bg-[#EDE8DC]/10 transition-all duration-200"
        >
          Support Festival 🤝
        </button>
      </div>
    </div>
  </div>

  {/* Bottom chevron */}
  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 animate-bounce">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
         stroke="#C9A84C" strokeWidth="2" opacity="0.5">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </div>
</section>


      {/* ══ ACTION CARDS ══════════════════════════════════════════ */}
      <section className="px-4 py-16 max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-2"
              style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
            Get Involved
          </h2>
          <p style={{ color:"rgba(237,232,220,0.4)", fontSize:"14px" }}>
            Be part of the Annual ERUE Festival of Uzairue Kingdom
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {ACTION_CARDS.map(card => (
            <button key={card.view} onClick={() => onNavigate(card.view)}
                    className="text-left p-6 rounded-2xl border transition-all group"
                    style={{ background:card.bg, borderColor:card.border }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.transform = "translateY(-4px)";
                      (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px ${card.color}20`;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                      (e.currentTarget as HTMLElement).style.boxShadow = "none";
                    }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                   style={{ background:`${card.color}15`, color:card.color }}>
                {card.icon}
              </div>
              <h3 className="font-bold text-base mb-2" style={{ color:"#EDE8DC" }}>{card.title}</h3>
              <p className="text-xs mb-4 leading-relaxed" style={{ color:"rgba(237,232,220,0.5)" }}>
                {card.desc}
              </p>
              <div className="flex items-center gap-1.5 text-xs font-semibold"
                   style={{ color:card.color }}>
                {card.cta} <ArrowRight size={13}/>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ══ FESTIVAL HIGHLIGHTS ═══════════════════════════════════ */}
      <section className="px-4 py-16"
               style={{ background:"rgba(14,24,18,0.6)", borderTop:"1px solid rgba(28,46,34,0.5)", borderBottom:"1px solid rgba(28,46,34,0.5)" }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-widest mb-2" style={{ color:"#C9A84C" }}>
              December 24–30, 2026
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-2"
                style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
              Festival Highlights
            </h2>
            <p style={{ color:"rgba(237,232,220,0.4)", fontSize:"14px" }}>
              7 days of culture, heritage, sports and celebration
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIGHLIGHTS.map(h => (
              <div key={h.title} className="flex items-start gap-4 p-5 rounded-2xl transition-all"
                   style={{ background:"rgba(7,13,10,0.8)", border:"1px solid rgba(28,46,34,0.8)" }}
                   onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor="rgba(201,168,76,0.2)"}
                   onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor="rgba(28,46,34,0.8)"}>
                <span className="text-3xl flex-shrink-0">{h.icon}</span>
                <div>
                  <h3 className="font-semibold text-sm mb-1" style={{ color:"#EDE8DC" }}>{h.title}</h3>
                  <p className="text-xs leading-relaxed" style={{ color:"rgba(237,232,220,0.45)" }}>{h.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <button onClick={() => onNavigate("programme")}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all border"
                    style={{ borderColor:"rgba(201,168,76,0.3)", color:"#C9A84C" }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background="rgba(201,168,76,0.08)"}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background="transparent"}>
              <Calendar size={15}/> View Full Programme <ChevronRight size={15}/>
            </button>
          </div>
        </div>
      </section>

      {/* ══ COMMUNITIES STRIP ════════════════════════════════════ */}
      <section className="px-4 py-16 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="md:w-1/2">
            <p className="text-xs uppercase tracking-widest mb-2" style={{ color:"#C9A84C" }}>
              Uzairue Kingdom
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-4"
                style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
              26 Communities,<br/>One Festival
            </h2>
            <p className="text-sm leading-relaxed mb-6" style={{ color:"rgba(237,232,220,0.5)" }}>
              All 22 villages of Uzairue Kingdom plus our Igala, Igbo, Yoruba and
              Diaspora communities come together for this magnificent annual celebration
              under the royal patronage of HRH The Ogieneni of Uzairue Kingdom.
            </p>
            <button onClick={() => onNavigate("communities")}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all"
                    style={{ background:"rgba(201,168,76,0.12)", border:"1px solid rgba(201,168,76,0.25)", color:"#C9A84C" }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background="rgba(201,168,76,0.2)"}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background="rgba(201,168,76,0.12)"}>
              <Globe size={15}/> Explore All Communities <ChevronRight size={15}/>
            </button>
          </div>
          <div className="md:w-1/2 grid grid-cols-4 gap-2">
            {["👑","🛡️","🌳","🌊","🎭","⚔️","🌿","🌾","🏺","🥊","⚖️","🧵","🏛️","⚒️","📚","🎪","🏪","🌿","🌲","🌴","⛰️","💰","🤝","🌟","🎺","✈️"].slice(0,16).map((icon,i) => (
              <div key={i} className="w-full aspect-square rounded-xl flex items-center justify-center text-2xl transition-all cursor-pointer"
                   style={{ background:"rgba(14,24,18,0.8)", border:"1px solid rgba(28,46,34,0.8)" }}
                   onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor="rgba(201,168,76,0.3)"}
                   onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor="rgba(28,46,34,0.8)"}>
                {icon}
              </div>
            ))}
          </div>
        </div>
      </section>

    
      <HomepageSections onNavigate={onNavigate} />
</div>
  );
}